import { useState } from "react";

const topPicks = [
  {
    id: "wool-ivory",
    name: "Ivory Wool Area Rug",
    description: "A hand-tufted wool rug in warm ivory — soft underfoot and beautifully textured in natural light.",
    priceRange: "Mid-range",
    buttonLabel: "View Rug Options",
    icon: "ri-layout-grid-line",
    tag: "Editor's Pick",
    affiliateUrl: "https://amzn.to/3Q04Ik5",
  },
  {
    id: "jute-natural",
    name: "Natural Jute Flatweave Rug",
    description: "A relaxed, earthy flatweave that adds texture and warmth without competing with the rest of the room.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "See Jute Rugs",
    icon: "ri-layout-grid-line",
    tag: "Most Natural",
    affiliateUrl: "https://amzn.to/41YkHld",
  },
  {
    id: "oat-looped",
    name: "Oat Looped Wool Rug",
    description: "A looped wool rug in a warm oat tone — subtle texture, durable, and endlessly versatile.",
    priceRange: "Mid-range",
    buttonLabel: "Explore Wool Rugs",
    icon: "ri-layout-grid-line",
    tag: "Most Versatile",
    affiliateUrl: "https://amzn.to/4dx4Ykf",
  },
];

const budgetRugs = [
  {
    id: "budget-1",
    name: "Affordable Neutral Cotton Rug",
    description: "A budget-friendly flatweave in warm neutral tones — easy to clean and surprisingly stylish.",
    priceRange: "Under $150",
    buttonLabel: "View Budget Rugs",
    icon: "ri-layout-grid-line",
    tag: "Best Value",
    affiliateUrl: "https://amzn.to/4chV9EB",
  },
  {
    id: "budget-2",
    name: "Simple Ivory Flatweave",
    description: "Clean, minimal, and warm — a great starting point for a calm neutral room.",
    priceRange: "Under $200",
    buttonLabel: "See Affordable Options",
    icon: "ri-layout-grid-line",
    tag: "Smart Pick",
    affiliateUrl: "",
  },
];

const midRangeRugs = [
  {
    id: "mid-1",
    name: "Hand-Tufted Wool Rug",
    description: "Quality construction and a warm neutral tone that improves with age — a rug worth investing in.",
    priceRange: "$300–$700",
    buttonLabel: "View Wool Rugs",
    icon: "ri-layout-grid-line",
    tag: "Anchor Piece",
    affiliateUrl: "https://amzn.to/4mmwljt",
  },
  {
    id: "mid-2",
    name: "Textured Sand Rug",
    description: "A soft sand tone with a subtle woven texture — grounding without being heavy.",
    priceRange: "$250–$600",
    buttonLabel: "Explore Mid-Range",
    icon: "ri-layout-grid-line",
    tag: "Most Textured",
    affiliateUrl: "",
  },
];

const splurgeRugs = [
  {
    id: "splurge-1",
    name: "Hand-Knotted Wool Rug",
    description: "A hand-knotted rug with exceptional depth and warmth — the kind that becomes a family heirloom.",
    priceRange: "$1,000+",
    buttonLabel: "View Luxury Rugs",
    icon: "ri-layout-grid-line",
    tag: "Heirloom Quality",
    affiliateUrl: "",
  },
  {
    id: "splurge-2",
    name: "Moroccan-Style Neutral Rug",
    description: "A quiet geometric pattern in warm ivory and sand — artisan-made and endlessly elegant.",
    priceRange: "$800+",
    buttonLabel: "Explore Premium Rugs",
    icon: "ri-layout-grid-line",
    tag: "Artisan Craft",
    affiliateUrl: "",
  },
];

const pairWith = [
  {
    id: "sofa",
    name: "Beige Linen Sofa",
    description: "The natural companion to a neutral rug — warm, textured, and effortlessly calm.",
    priceRange: "Mid-range",
    buttonLabel: "View Sofa Options",
    icon: "ri-sofa-line",
    tag: "Perfect Pair",
    affiliateUrl: "https://amzn.to/4sd5Qyh",
  },
  {
    id: "table",
    name: "Light Wood Coffee Table",
    description: "A warm wood table that sits beautifully on a neutral rug and anchors the seating area.",
    priceRange: "Mid-range",
    buttonLabel: "Explore Coffee Tables",
    icon: "ri-table-line",
    tag: "Finishing Touch",
    affiliateUrl: "https://amzn.to/4tANU1D",
  },
  {
    id: "curtains",
    name: "Linen Curtains",
    description: "Soft, airy curtains that echo the warmth of the rug and complete the neutral palette.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "See Curtain Options",
    icon: "ri-layout-column-line",
    tag: "Easy Upgrade",
    affiliateUrl: "https://amzn.to/4tz8yPK",
  },
  {
    id: "lamp",
    name: "Warm Floor Lamp",
    description: "A floor lamp nearby adds ambient warmth that brings out the texture of the rug.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "View Lamp Options",
    icon: "ri-lightbulb-line",
    tag: "Mood Setter",
    affiliateUrl: "https://amzn.to/3O1OR3W",
  },
];

function ProductCard({
  name, description, priceRange, buttonLabel, icon, tag, affiliateUrl,
}: {
  name: string; description: string; priceRange: string; buttonLabel: string;
  icon: string; tag: string; affiliateUrl: string;
}) {
  const [hovered, setHovered] = useState(false);
  const hasLink = affiliateUrl.trim().length > 0;

  return (
    <div
      className="group border border-[#EDE6DA] rounded-xl p-6 bg-[#FDFAF5] hover:border-[#C8BAA8] transition-all duration-300 flex flex-col gap-4"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F1E8]">
          <i className={`${icon} text-[#8B8378] text-base`} />
        </div>
        <span
          className="text-[#B5A898] text-[9px] tracking-[0.35em] uppercase border border-[#EDE6DA] px-2.5 py-1 rounded-full"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {tag}
        </span>
      </div>
      <h3
        className="text-[#2C2825] text-lg leading-snug"
        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
      >
        {name}
      </h3>
      <p
        className="text-[#8B8378] text-sm leading-relaxed flex-1"
        style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
      >
        {description}
      </p>
      <div className="flex items-center gap-2">
        <span className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>Price</span>
        <span className="w-4 h-px bg-[#EDE6DA]" />
        <span className="text-[#6B5E53] text-xs" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{priceRange}</span>
      </div>
      {hasLink ? (
        <a
          href={affiliateUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#C8BAA8] text-[#6B5E53] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] hover:bg-[#2C2825] hover:text-white hover:border-[#2C2825] transition-all duration-300 cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={`ri-arrow-right-line text-[10px] transition-transform duration-200 ${hovered ? "translate-x-0.5" : ""}`} />
          </span>
        </a>
      ) : (
        <div
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#EDE6DA] text-[#C8BAA8] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] whitespace-nowrap select-none"
          style={{ fontFamily: "'Inter', sans-serif" }}
          title="Link coming soon"
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-time-line text-[10px]" />
          </span>
        </div>
      )}
    </div>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-5 mb-10">
      <div className="flex-1 h-px bg-[#EDE6DA]" />
      <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase flex-shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>{label}</p>
      <div className="flex-1 h-px bg-[#EDE6DA]" />
    </div>
  );
}

function ProductGrid({ products, cols = 2 }: { products: typeof topPicks; cols?: 2 | 3 }) {
  return (
    <div className={`grid grid-cols-1 ${cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-5`}>
      {products.map((p) => <ProductCard key={p.id} {...p} />)}
    </div>
  );
}

export default function NRShopSection() {
  return (
    <section className="max-w-2xl mx-auto px-4 md:px-0 py-4 mb-16">

      <div id="top-picks" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Rug Edit</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Our Top Neutral Rug Picks
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          These are the neutral rugs we keep recommending — chosen for their warm tone, natural material, and ability to ground a calm room without overpowering it.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={topPicks} cols={3} />
      </div>

      <SectionDivider label="By Budget" />

      <div id="budget-rugs" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Budget</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Best Neutral Rugs Under $200
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          A well-chosen neutral rug does not have to be expensive. These picks keep the tone warm and the look considered.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={budgetRugs} cols={2} />
      </div>

      <div id="mid-range-rugs" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Mid-Range</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Elevated Neutral Rugs $250–$700
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          In this range, material quality and construction improve noticeably. These rugs are built to last and styled to feel genuinely refined.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={midRangeRugs} cols={2} />
      </div>

      <div id="splurge-rugs" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Luxury</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Splurge Picks — Luxury Neutral Rugs
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          For a more elevated result, these rugs offer exceptional craftsmanship, considered texture, and the kind of quiet presence that makes a room feel complete.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={splurgeRugs} cols={2} />
      </div>

      <SectionDivider label="Complete the Look" />

      <div id="pair-with" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Style It</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          What to Pair With a Neutral Rug
        </h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          A neutral rug works best when the rest of the room stays warm and layered. These are the pieces that complete the look.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={pairWith} cols={2} />
      </div>

    </section>
  );
}
